import { api } from "../api-client.js";
import { renderEnvTabs, type Env } from "../components/env-tabs.js";
import { renderEmptyStateBanner, type ScreenState } from "../components/empty-state-banner.js";

type WatchlistReason = "HOLDING_TOO_LONG" | "BUY_STEPS_EXHAUSTED" | "BOTH";

interface WatchlistItem {
  stock_code: string;
  stock_name: string;
  holding_days: number;
  reason: WatchlistReason;
  buy_steps_used: number | null;
  buy_steps_max: number | null;
}

interface WatchlistResult {
  env: Env;
  state: ScreenState;
  as_of_date: string | null;
  items: WatchlistItem[];
}

const REASON_LABEL: Record<WatchlistReason, string> = {
  HOLDING_TOO_LONG: "장기보유",
  BUY_STEPS_EXHAUSTED: "분할매수 소진",
  BOTH: "장기보유 + 분할매수 소진",
};

function fmtSteps(used: number | null, max: number | null): string {
  if (used === null || max === null) return "—";
  return `${used} / ${max}`;
}

// 004 §A-5 v1.5: DAILY_SUMMARY.watchlist — 10일 이상 보유했거나 분할매수 차수를 다 쓰고도
// 안 판 "관리종목" 목록. 과거 이력이 아니라 최신 스냅샷(오늘 또는 마지막 수신일 기준) 하나만 보여준다
// (사용자 요청: 할 일 목록 성격이라 날짜별로 되짚어볼 필요는 없다고 판단).
export async function renderWatchlist(container: HTMLElement): Promise<void> {
  let env: Env = "운영";

  // env 탭을 바꿀 때마다 화면 전체를 지웠다가 다시 그리면 응답을 기다리는 동안 깜빡였다 — 제목/탭은
  // 슬롯에 담아 한 번만 배치하고, 결과 영역은 새 내용이 다 준비된 뒤 한 번에 교체한다.
  container.innerHTML = `<h2 class="page-title">관리종목</h2>`;
  const tabsSlot = document.createElement("div");
  const resultsSlot = document.createElement("div");
  container.append(tabsSlot, resultsSlot);

  async function load(): Promise<void> {
    tabsSlot.replaceChildren(
      renderEnvTabs(env, (newEnv) => {
        env = newEnv;
        void load();
      }),
    );

    let result: WatchlistResult;
    try {
      result = await api.get<WatchlistResult>(`/api/watchlist?env=${encodeURIComponent(env)}`);
    } catch {
      const err = document.createElement("p");
      err.textContent = "데이터를 불러오지 못했습니다.";
      resultsSlot.replaceChildren(err);
      return;
    }

    const frag = document.createDocumentFragment();

    const banner = renderEmptyStateBanner(result.state, env, (newEnv) => {
      env = newEnv;
      void load();
    });
    if (banner) frag.appendChild(banner);

    if (result.state === "OK") {
      if (result.as_of_date) {
        const asOf = document.createElement("p");
        asOf.style.color = "var(--text-dim)";
        asOf.style.marginBottom = "12px";
        asOf.textContent = `${result.as_of_date} 기준`;
        frag.appendChild(asOf);
      }

      const card = document.createElement("div");
      card.className = "card";

      if (result.items.length === 0) {
        card.innerHTML = `<p style="color:var(--text-dim)">현재 관리가 필요한 종목이 없습니다.</p>`;
      } else {
        const scrollWrap = document.createElement("div");
        scrollWrap.className = "table-scroll";
        const table = document.createElement("table");
        table.innerHTML = `
          <thead>
            <tr><th>종목</th><th>보유일수</th><th>사유</th><th>분할매수(진행/최대)</th></tr>
          </thead>
          <tbody>
            ${result.items
              .map(
                (w) => `
              <tr>
                <td>${w.stock_name} <span style="color:var(--text-dim)">${w.stock_code}</span></td>
                <td>${w.holding_days}일</td>
                <td>${REASON_LABEL[w.reason]}</td>
                <td>${fmtSteps(w.buy_steps_used, w.buy_steps_max)}</td>
              </tr>`,
              )
              .join("")}
          </tbody>
        `;
        scrollWrap.appendChild(table);
        card.appendChild(scrollWrap);
      }
      frag.appendChild(card);
    }

    resultsSlot.replaceChildren(frag);
  }

  await load();
}
